import { BehaviorSubject, Observable } from 'rxjs';

import { BackendObjectRef } from '../lib/backend-integration.service';
import { FormField } from '../lib/form-field.model';
import { FormFieldTypeGeneral, FormFieldTypeUserField } from '../lib/form-field-type.model';
import { Form } from '../lib/form.model';
import { FormPermission } from '../lib/form-permission.model';
import { State } from '../lib/state.model';
import { WorkflowEditorStore } from '../lib/workflow-editor.store';

/**
 * Representation of user choice of the role & state for the preview
 * or permission assignment
 */
export interface FormEditorRoleStateSettings {
  readonly role: string;
  readonly state: string;
}

export class FormEditorService {
  private _stateRoleSettings$$ =
    new BehaviorSubject<FormEditorRoleStateSettings|undefined>(undefined);
  public get stateRoleSettings$(): Observable<FormEditorRoleStateSettings|undefined> {
    return this._stateRoleSettings$$.asObservable();
  }
  public get stateRoleSettings(): FormEditorRoleStateSettings|undefined {
    return this._stateRoleSettings$$.value;
  }
  public set stateRoleSettings(value: FormEditorRoleStateSettings|undefined) {
    this._stateRoleSettings$$.next(value);
  }

  private _form$$: BehaviorSubject<Form>;
  public get form$(): Observable<Form> { return this._form$$.asObservable(); }
  public get form(): Form { return this._form$$.value; }
  public set form(value: Form) { this._form$$.next(value); }

  public availableStates: State[] = [];

  public get availableStatesSorted(): State[] {
    return this.availableStates.sort((a, b) => a.name.localeCompare(b.name));
  }

  public availableRoles: BackendObjectRef[];

  public workflowRoles: BackendObjectRef[] = [];

  public availableUserFields: FormFieldTypeUserField[];

  public availableGeneralFields: FormFieldTypeUserField[];
  public get availableFieldTypes(): (FormFieldTypeGeneral|FormFieldTypeUserField)[] {
    return [
      ...this.availableGeneralFields,
      ...this.availableUserFields
    ];
  }

  public get allFormFields(): FormField[] {
    return this.form.fields;
  }

  private _workflowEditor: WorkflowEditorStore;

  public constructor(workflowEditor: WorkflowEditorStore) {
    this._workflowEditor = workflowEditor;

    this.availableRoles = this._workflowEditor.availableRoles;
    this.availableGeneralFields = this._workflowEditor.availableGeneralFields;
    this.availableUserFields = this._workflowEditor.availableUserFields;

    const form = this._workflowEditor.workflow?.form
                 ? this._workflowEditor.workflow?.form
                 : new Form(undefined, '');
    this._form$$ = new BehaviorSubject<Form>(form);

    this._workflowEditor.workflow$
                        .subscribe(workflow => {
                          if (!workflow) { return; }
                          if (!workflow.form) { return; }
                          this.form = workflow.form;
                          this.availableStates = workflow.states;
                          this.workflowRoles = workflow.roles;
                        });
  }

  public addFormField(value: FormField): void {
    const form = this.form.copy();
    form.addFieldAt(value);
    this.form = form;
  }

  public deleteFormField(id: string): void {
    const form = this.form.copy();
    form.deleteField(id);
    this.form = form;
  }

  public moveFieldUp(id: string): void {
    const from = this.form.findFieldIndexByID(id);
    if (from === -1) { return; }

    this.moveField(from, from - 1);
  }

  public moveFieldDown(id: string): void {
    const from = this.form.findFieldIndexByID(id);
    if (from === -1) { return; }

    this.moveField(from, from + 1);
  }

  public moveFieldTo(id: string, to: number): void {
    const from = this.form.findFieldIndexByID(id);
    if (from === -1) { return; }

    this.moveField(from, to);
  }

  public moveField(from: number, to: number): void {
    const form = this.form.copy();
    try { form.moveField(from, to); }
    catch {}
    this.form = form;
  }

  public patchFormField(id: string, value: Partial<FormField>): void {
    const form = this.form.copy();
    form.patchField(id, value);
    this.form = form;
  }

  public setFormFieldsPermission(fields: FormField[],
                                 visible: boolean,
                                 editable: boolean): void {
    if (!this.stateRoleSettings) { return; }
    const role = this.stateRoleSettings.role;
    const state = this.stateRoleSettings.state;

    const form = this.form.copy();
    fields.forEach(field => {
             if (!this.stateRoleSettings) { return; }
             const permission =
               new FormPermission(field.id, role, state, visible, editable);
             form.setPermission(permission);
           });
    this.form = form;
  }

  public setFormFieldPermission(field: FormField,
                                visible: boolean,
                                editable: boolean): void {
    this.setFormFieldsPermission([field], visible, editable);
  }

  public removeFormFieldPermission(fieldID: string): void {
    if (!this.stateRoleSettings) { return; }
    const field = this.form.findFieldByID(fieldID);
    if (!field) { return; }

    const permission =
      new FormPermission(fieldID,
                         this.stateRoleSettings.role,
                         this.stateRoleSettings.state);
    const form = this.form.copy();
    form.deletePermission(permission);
    this.form = form;
  }

  /**
   * Finds permissions for a Field
   *
   * This method calls `findPermissionsBy` on the Form model so has the same
   * functionality with following exceptions:
   *
   * 1) When Optional parameters are not provided it uses `stateRoleSettings`
   *    as defaults.
   *
   * 2) When both optional parameters are not provided or there is no settings
   *    it returns NO PERMISSIONS. Otherwise we would get all permissions for
   *    the specified field.
   *
   * @param fieldID Field to get permissions for
   * @param roleID Optional permission role
   * @param stateID Optional permission state
   */
  public findPermissionsForField(fieldID: string,
                                 roleID?: string,
                                 stateID?: string): FormPermission[] {
    const role = roleID ?? this.stateRoleSettings?.role;
    const state = stateID ?? this.stateRoleSettings?.state;
    if (!role && !state) { return []; }
    return this.form.findPermissionsBy(fieldID, role, state);
  }
}
